import React, { useState } from 'react'
import { useExpense } from '../contexts/ExpenseContext'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from 'recharts'
import { Download, TrendingUp, TrendingDown, Repeat } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import ExportModal from '../components/ExportModal'
import { formatRupees } from '../utils/currency'

const StatsPage = () => {
  const { expenses, stats, loading, exportExpenses } = useExpense()
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  const recurringProjection = stats.recurringProjection || {
    monthlyEquivalent: 0,
    byFrequency: { daily: 0, weekly: 0, monthly: 0, yearly: 0 }
  }

  // Colors for charts
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4']

  // Prepare chart data
  const categoryData = Object.entries(stats.categoryBreakdown).map(([category, amount]) => ({
    name: category,
    value: amount
  }))

  // Enhanced monthly data calculation
  const getMonthlyData = () => {
    const monthlyExpenses = {}
    const now = new Date()
    
    // Initialize last 12 months with zero
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = date.toISOString().substr(0, 7) // YYYY-MM format
      const displayName = date.toLocaleDateString('en-US', { 
        month: 'short', 
        year: '2-digit' 
      })
      monthlyExpenses[key] = { amount: 0, displayName, count: 0 }
    }
    
    // Add actual expenses
    expenses.forEach(expense => {
      const expenseDate = new Date(expense.created_at)
      const monthKey = expenseDate.toISOString().substr(0, 7)
      
      if (monthlyExpenses[monthKey]) {
        monthlyExpenses[monthKey].amount += parseFloat(expense.amount)
        monthlyExpenses[monthKey].count += 1
      }
    })
    
    // Convert to sorted array and filter out empty months at the beginning
    const sortedData = Object.entries(monthlyExpenses)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, data]) => ({
        month: data.displayName,
        amount: Math.round(data.amount * 100) / 100, // Round to 2 decimals
        count: data.count
      }))
    
    // Find first month with expenses and return from there
    const firstExpenseIndex = sortedData.findIndex(item => item.amount > 0)
    return firstExpenseIndex >= 0 ? sortedData.slice(firstExpenseIndex) : sortedData.slice(-6)
  }

  const monthlyData = getMonthlyData()

  // Daily spending (last 7 days)
  const getDailyData = () => {
    const dailyExpenses = {}
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - i)
      return date.toISOString().split('T')[0]
    }).reverse()

    last7Days.forEach(date => {
      dailyExpenses[date] = 0
    })

    expenses.forEach(expense => {
      const date = expense.created_at.split('T')[0]
      if (dailyExpenses.hasOwnProperty(date)) {
        dailyExpenses[date] += parseFloat(expense.amount)
      }
    })

    return Object.entries(dailyExpenses).map(([date, amount]) => ({
      date: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
      amount
    }))
  }

  const dailyData = getDailyData()

  const handleExport = async (format, filename) => {
    try {
      setExportLoading(true)
      await exportExpenses(format, filename)
      setShowExportModal(false)
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setExportLoading(false)
    }
  }

  // Top categories
  const topCategories = Object.entries(stats.categoryBreakdown)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)

  if (loading) {
    return <LoadingSpinner message="Loading statistics..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Statistics
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Analyze your spending patterns and trends
          </p>
        </div>
        
        <button
          onClick={() => setShowExportModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Download className="h-4 w-4" />
          <span>Export Data</span>
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Expenses
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatRupees(stats.total)}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-primary-600" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Transactions
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.count}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Average per Transaction
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatRupees(stats.count > 0 ? (stats.total / stats.count) : 0)}
              </p>
            </div>
            <TrendingDown className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Recurring Monthly Projection
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatRupees(recurringProjection.monthlyEquivalent || 0)}
              </p>
            </div>
            <Repeat className="h-8 w-8 text-indigo-600" />
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Based on your fixed daily/weekly/monthly/yearly recurring expenses
          </p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Breakdown */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Spending by Category
          </h3>
          {categoryData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatRupees(value), 'Amount']} />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Legend */}
              <div className="mt-4 space-y-2">
                {categoryData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span className="text-gray-700 dark:text-gray-300">{entry.name}</span>
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatRupees(entry.value)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No data available
            </div>
          )}
        </div>

        {/* Monthly Trend */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Monthly Spending Trend
          </h3>
          {monthlyData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name, props) => [
                      formatRupees(value), 
                      'Total Spent',
                      `${props.payload.count} expenses`
                    ]}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Bar dataKey="amount" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              
              {/* Monthly Summary */}
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {formatRupees(monthlyData.reduce((sum, month) => sum + month.amount, 0))}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {formatRupees(monthlyData.reduce((sum, month) => sum + month.amount, 0) / monthlyData.filter(m => m.amount > 0).length || 0)}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Average</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {Math.max(...monthlyData.map(m => m.amount)) === Math.min(...monthlyData.map(m => m.amount)) ? 
                        '0%' : 
                        `${Math.round(((monthlyData[monthlyData.length - 1]?.amount || 0) - (monthlyData[monthlyData.length - 2]?.amount || 0)) / (monthlyData[monthlyData.length - 2]?.amount || 1) * 100)}%`
                      }
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Last Month</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Daily Spending & Top Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Spending (Last 7 Days) */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Daily Spending (Last 7 Days)
          </h3>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => [formatRupees(value), 'Amount']} />
                <Line 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No data available
            </div>
          )}
        </div>

        {/* Top Categories */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Top Categories
          </h3>
          {topCategories.length > 0 ? (
            <div className="space-y-4">
              {topCategories.map(([category, amount], index) => {
                const percentage = (amount / stats.total) * 100
                return (
                  <div key={category} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {category}
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatRupees(amount)} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: COLORS[index % COLORS.length]
                        }}
                      ></div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No categories found
            </div>
          )}
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isLoading={exportLoading}
        title="Export Statistics Data"
      />
    </div>
  )
}

export default StatsPage
